import { ASSIGNEE_UNASSIGNED_LABEL } from '@/constants/listGridFilters';
import { normalizePhoneForFilter, stripUnedifyingCharacters } from '@/util';
import { GridFilterItem } from '@mui/x-data-grid';

const isUnassignedAssigneeFilterValue = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return (
    stripUnedifyingCharacters(value).trim().toLowerCase() ===
    ASSIGNEE_UNASSIGNED_LABEL.toLowerCase()
  );
};

/** Map Assigned-to filters that use the UI label "Unassigned" to Redis empty/non-empty assigneeName. */
export const mapAssigneeNameFilterForListApi = (item: GridFilterItem): GridFilterItem => {
  const operator = item.operator ?? '';

  if (operator === 'equals' || operator === 'contains') {
    if (isUnassignedAssigneeFilterValue(item.value)) {
      return { ...item, operator: 'isEmpty', value: '' };
    }
  }
  if (operator === 'doesNotEqual' || operator === 'doesNotContain') {
    if (isUnassignedAssigneeFilterValue(item.value)) {
      return { ...item, operator: 'isNotEmpty', value: '' };
    }
  }

  return item;
};

/** Redis list contact phone filters: digits only (spaces/formatting are not indexed). */
export const mapPhoneFilterForListApi = (item: GridFilterItem): GridFilterItem => {
  if (item.field !== 'phone' || ['isEmpty', 'isNotEmpty'].includes(item.operator ?? '')) {
    return item;
  }
  if (Array.isArray(item.value)) {
    return {
      ...item,
      value: item.value.map((entry) =>
        typeof entry === 'string' ? normalizePhoneForFilter(entry) : entry
      ),
    };
  }
  if (typeof item.value === 'string') {
    return { ...item, value: normalizePhoneForFilter(item.value) };
  }
  return item;
};

/** Whether a DataGrid filter item should be sent to GET /lists/:id (server-side). */
export const isGridFilterItemActive = (item: GridFilterItem): boolean => {
  if (['isEmpty', 'isNotEmpty'].includes(item.operator ?? '')) {
    return true;
  }
  const { value } = item;
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }
  if (typeof value === 'string') {
    if (['contains', 'startsWith', 'endsWith'].includes(item.operator ?? '')) {
      return value.length > 1;
    }
    return value.length > 0;
  }
  return false;
};

/** Map grid column fields to Redis list contact fields for server-side filtering. */
export const mapGridFilterItemForListApi = (item: GridFilterItem): GridFilterItem => {
  const mapped = item.field === 'id' ? { ...item, field: 'contactId' } : item;
  if (mapped.field === 'assigneeName') {
    return mapAssigneeNameFilterForListApi(mapped);
  }
  if (mapped.field === 'phone') {
    return mapPhoneFilterForListApi(mapped);
  }
  return mapped;
};

const ID_NUMERIC_COMPARISON_OPERATORS = new Set(['=', '!=', '<', '>', '<=', '>=']);

const normalizeTextFilterValueForApi = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  return value.trim().replace(/\s+/g, ' ');
};

const coerceIdFilterScalarToNumberForApi = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }
  return value;
};

/** Map id filter values to the shape Redis expects (see ListContact/filters.ts). */
const mapIdFilterValueForApi = (operator: string, value: unknown): unknown => {
  if (operator === 'isAnyOf') {
    if (!Array.isArray(value)) {
      return value;
    }
    return value
      .map((entry) => (typeof entry === 'number' ? String(entry) : String(entry).trim()))
      .filter((entry) => entry.length > 0);
  }
  if (ID_NUMERIC_COMPARISON_OPERATORS.has(operator)) {
    if (Array.isArray(value)) {
      return value.map(coerceIdFilterScalarToNumberForApi);
    }
    return coerceIdFilterScalarToNumberForApi(value);
  }
  return normalizeTextFilterValueForApi(value);
};

/** Serialize one filter for the GET /lists/:id `filter` query param (must be valid JSON). */
export const serializeListFilterForApi = (item: GridFilterItem): string => {
  const mapped = mapGridFilterItemForListApi(item);
  const operator = mapped.operator ?? '';
  const isIdField = mapped.field === 'contactId' || mapped.field === 'id';
  const payload = {
    field: mapped.field,
    operator,
    value: ['isEmpty', 'isNotEmpty'].includes(operator)
      ? ''
      : isIdField
        ? mapIdFilterValueForApi(operator, mapped.value)
        : normalizeTextFilterValueForApi(mapped.value),
  };
  return JSON.stringify(payload);
};

/** Map grid sort column to a Redis list contact field. */
export const mapListSortColumnForApi = (sortColumn: string): string =>
  sortColumn === 'id' ? 'contactIdNumber' : sortColumn;
